import React, { useState } from 'react';
import { cn } from '../../utils/cn';

interface TooltipProps {
  content: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Tooltip component with info icon
 * Displays tooltip content on hover (desktop) or tap (mobile)
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  className,
  position = 'top'
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div className={cn('relative inline-block', className)}>
      <div
        className="cursor-help"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        onTouchStart={() => setIsVisible(!isVisible)}
      >
        {children || (
          <div className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-xs">
            i
          </div>
        )}
      </div>

      {isVisible && (
        <>
          {/* Backdrop for mobile tap-outside-to-close */}
          <div
            className="fixed inset-0 z-40 md:hidden"
            onClick={() => setIsVisible(false)}
          />

          {/* Tooltip content */}
          <div
            className={cn(
              'absolute z-[9999] max-w-[85vw] sm:w-64 md:w-72 lg:w-80 p-4 text-sm',
              'bg-popover text-popover-foreground opacity-100',
              'border border-border rounded-md shadow-lg',
              'animate-in fade-in-0 zoom-in-95',
              'break-words whitespace-normal',
              positionClasses[position]
            )}
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word'
            }}
          >
            {content}
          </div>
        </>
      )}
    </div>
  );
};

interface InfoIconProps {
  className?: string;
}

/**
 * Circled "i" info icon component
 * To be used as trigger for tooltips
 */
export const InfoIcon: React.FC<InfoIconProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center',
        'w-4 h-4 rounded-full',
        'border border-current',
        'text-xs font-medium',
        'text-muted-foreground hover:text-foreground',
        'transition-colors duration-150 ease-theme',
        className
      )}
    >
      i
    </div>
  );
};

export default Tooltip;
